
import { useScrollReveal } from '../../../hooks/useScrollReveal';
import { useState, useEffect, useRef } from 'react';

function AnimatedNumber({ target, suffix = '', isVisible }: { target: number; suffix: string; isVisible: boolean }) {
  const [count, setCount] = useState(0);
  const hasAnimated = useRef(false);

  useEffect(() => {
    if (!isVisible || hasAnimated.current) return;
    hasAnimated.current = true;
    const duration = 2000;
    const steps = 60;
    const increment = target / steps;
    let current = 0;
    const timer = setInterval(() => {
      current += increment;
      if (current >= target) {
        setCount(target);
        clearInterval(timer);
      } else {
        setCount(Math.floor(current));
      }
    }, duration / steps);
    return () => clearInterval(timer);
  }, [isVisible, target]);

  return <span>{count}{suffix}</span>;
}

export default function Results() {
  const { ref, isVisible } = useScrollReveal(0.15);

  const metrics = [
    { value: 40, suffix: '%+', label: 'Operational Efficiency Gains', icon: 'ri-speed-up-line' },
    { value: 30, suffix: '%+', label: 'Lead-to-Client Conversion Lift', icon: 'ri-user-follow-line' },
    { value: 24, suffix: '/7', label: 'Automated Workflow Control', icon: 'ri-time-line' },
  ];

  return (
    <section id="results" className="relative py-36 px-6 bg-slate-950 overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0">
        <img
          src="https://public.readdy.ai/ai/img_res/234153650b7b0e1f3ea2d04aec633e7d.jpg"
          alt="Results background"
          className="w-full h-full object-cover object-top opacity-15"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-slate-950 via-slate-950/85 to-slate-950" />
      </div>

      <div ref={ref} className="relative z-10 max-w-6xl mx-auto">
        {/* Section Header */}
        <div className={`text-center mb-20 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <span className="text-teal-400 text-xs font-medium tracking-widest uppercase mb-4 block">Proven Results</span>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-5 tracking-tight">
            Built for Performance-Driven Operators
          </h2>
        </div>

        {/* Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-20">
          {metrics.map((metric, index) => (
            <div
              key={index}
              className={`relative group text-center p-10 rounded-2xl bg-slate-900/40 border border-slate-800/40 hover:border-teal-500/20 transition-all duration-700 cursor-pointer ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
              style={{ transitionDelay: `${(index + 1) * 150}ms` }}
            >
              <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-teal-500/10 border border-teal-500/15 mx-auto mb-6">
                <i className={`${metric.icon} text-xl text-teal-400`}></i>
              </div>
              <div className="text-6xl md:text-7xl font-bold text-white mb-4 group-hover:text-teal-400 transition-colors duration-500">
                <AnimatedNumber target={metric.value} suffix={metric.suffix} isVisible={isVisible} />
              </div>
              <div className="h-px w-16 bg-gradient-to-r from-transparent via-teal-500/50 to-transparent mx-auto mb-5" />
              <p className="text-sm text-gray-400">{metric.label}</p>
            </div>
          ))}
        </div>

        {/* Subtext */}
        <div className={`text-center transition-all duration-700 delay-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
          <p className="text-xl text-gray-400 font-light">
            Our systems don&apos;t add noise. <strong className="text-white font-semibold">They create structure.</strong>
          </p>
        </div>
      </div>
    </section>
  );
}
